// Runtime-derived cross-origin URL config.
//
// The SPA is shipped as a single env-agnostic bundle, deployed once per CF
// Pages project per env. Backend hostnames vary per env (api.myworkpay.site
// in dev, api.workpay.site in staging, api.<prod-apex> in prod), so they're
// computed from window.location.hostname rather than baked in at build time.
//
// Shape: a tenant SPA always loads from `<tenant>.<apex>` (e.g.
// `master.myworkpay.site`). Strip the first DNS label and the rest IS the
// apex. The sibling cross-origin backend services live at `api.<apex>`,
// `auth.<apex>`, `external.<apex>` — same apex across every host the user
// can reach.
//
// Local-dev path: `window.location.hostname` is `localhost` (no dot) →
// `apex` resolves to null → URLs collapse to relative/localhost fallbacks
// so the Vite dev server's `/api` proxy + local docker-compose continue to
// work without env-var ceremony.
//
// This file replaces the previous build-time scheme that read VITE_API_BASE_URL
// / VITE_ONLYOFFICE_URL / VITE_DS_EXTERNAL_URL from .env.production. Those
// vars hardcoded `myworkpay.site` hostnames into the bundle, which broke
// once each env got its own apex (dev=myworkpay.site, staging=workpay.site,
// prod=TBD). See the env-apex migration plan for the full design.

function computeApex(): string | null {
  if (typeof window === 'undefined' || !window.location) return null;
  const host = window.location.hostname;
  // Local-dev shapes — every form collapses to "no apex, use fallbacks":
  //   localhost                 — `pnpm dev` default
  //   127.0.0.1 / numeric IPv4  — direct-IP access
  //   ::1 / [::1]               — IPv6 loopback (browsers normalise to ::1)
  //   master.localhost / *.localhost — /etc/hosts multi-tenant debug
  if (host === 'localhost' || host === '::1' || host.endsWith('.localhost')) return null;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null;
  // Single-label host with no dot at all (rare — internal dev hostnames).
  if (!host.includes('.')) return null;
  // master.myworkpay.site → myworkpay.site
  // admin.workpay.site    → workpay.site
  return host.split('.').slice(1).join('.');
}

const APEX = computeApex();

/**
 * API origin (scheme+host, no path) for cross-origin BFF + service calls.
 * Empty string in local dev so axios baseURL stays same-origin (Vite proxy
 * forwards `/api/*` to the local BFF). Otherwise resolves to
 * `https://api.<apex>` for the env the SPA is loaded from.
 */
export const API_BASE: string = APEX ? `https://api.${APEX}` : '';

/**
 * OnlyOffice Document Server public URL — embedded by the editor service
 * when launching the OnlyOffice client SDK. Falls back to the
 * docker-compose dev stack on localhost when no apex is detectable.
 */
export const ONLYOFFICE_URL: string = APEX ? `https://api.${APEX}/office` : 'http://localhost:8088';

/**
 * ds-external (review/sign portal) public URL — used by snapshot-admin to
 * build copy-this-review-link URLs. Falls back to the local ds-external
 * dev server on localhost.
 */
export const DS_EXTERNAL_URL: string = APEX ? `https://external.${APEX}` : 'http://localhost:3002';

// Dev fallback apex for subdomain previews — `APEX` is null on localhost, but a
// tenant URL preview should still show a real apex (the dev env's) rather than
// "acme.localhost".
const TENANT_APEX_FALLBACK = 'myworkpay.site';

/**
 * Apex used to render a tenant's subdomain URL preview (e.g. the
 * "acme.myworkpay.site" shown on the clients list / tenant profile). Tracks
 * the live apex per env; falls back to the dev apex on localhost.
 *
 * DISPLAY-ONLY. These previews never drive a request or navigation, so the
 * localhost fallback being the dev apex is cosmetic. Do NOT reuse this (or
 * `TENANT_DOMAIN_SUFFIX` / `tenantHost`) to build a navigable URL without
 * revisiting the fallback — a `localhost`→staging-backend session would render
 * `.myworkpay.site` while the real tenant host is `.workpay.site`.
 */
export const TENANT_DOMAIN_APEX: string = APEX ?? TENANT_APEX_FALLBACK;

/** Bare subdomain suffix incl. leading dot, e.g. ".myworkpay.site" — for input adornments. */
export const TENANT_DOMAIN_SUFFIX: string = `.${TENANT_DOMAIN_APEX}`;

/**
 * Build a tenant's full host from its subdomain label, e.g. "acme" → "acme.myworkpay.site".
 * Assumes `subdomain` is an already-validated DNS label (callers gate on a
 * truthy value); it is not trimmed or sanitised here.
 */
export function tenantHost(subdomain: string): string {
  return `${subdomain}${TENANT_DOMAIN_SUFFIX}`;
}
